import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'

import { CheckRequiredFields } from '../../../../../components/CheckRequiredFields'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { useDeliveryForecastById } from '../../../data'
import { Divisions } from './Divisions'

type Props = {
  deliveryForecastId: string
  visible: boolean
  setVisible: React.Dispatch<boolean>
  handleClose: () => void
  isRequestRunning: boolean
  onSubmit: () => void
  isConfirmationActive: boolean
}

export const AcceptDeliveryForecastView: React.FC<Props> = ({
  deliveryForecastId,
  visible,
  setVisible,
  handleClose,
  isRequestRunning,
  onSubmit,
  isConfirmationActive,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()
  const { data: deliveryForecast } = useDeliveryForecastById(deliveryForecastId)

  // ************************************************************
  // components
  // ************************************************************
  // confirmationId
  const confirmationIdLabel = (
    <label htmlFor="confirmationId" className="text-sm"></label>
  )

  const confirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={deliveryForecast?.Payload.ConfirmationId}
      className="w-full"
      disabled
    />
  )

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.acceptDeliveryForecast}
      component={confirmationIdComponent}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // confirmationDate
  const confirmationDateLabel = (
    <label htmlFor="confirmationDate" className="text-sm"></label>
  )

  const currentDate = new Date()

  const confirmationDateComponent = (
    <Calendar
      id="confirmationDate"
      value={currentDate}
      className="w-full"
      readOnlyInput
      locale={language}
      disabled
    />
  )

  const confirmationDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.acceptDeliveryForecast}
      component={confirmationDateComponent}
      label={confirmationDateLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // ************************************************************
  // elements
  // ************************************************************
  // dialog
  const header = (
    <>
      {isConfirmationActive
        ? t('deliveryForecast.title.send.dialog.title.withConfirmation')
        : t('deliveryForecast.title.send.dialog.title.withoutConfirmation')}
    </>
  )

  const footer = (
    <>
      <Button
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={
          isConfirmationActive
            ? t(
                'deliveryForecast.title.send.dialog.send.label.withConfirmation',
              )
            : t(
                'deliveryForecast.title.send.dialog.send.label.withoutConfirmation',
              )
        }
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={onSubmit}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      <CheckRequiredFields data={deliveryForecast}>
        {(isDisabled, tooltip) => (
          <>
            {isConfirmationActive && tooltip}
            <Button
              label={
                isConfirmationActive
                  ? t('deliveryForecast.title.send.label.withConfirmation')
                  : t('deliveryForecast.title.send.label.withoutConfirmation')
              }
              icon="pi pi-check"
              className="send-button"
              onClick={() => setVisible(true)}
              visible={
                config.deliveryForecast.acceptDeliveryForecast.enabled !== false
              }
              disabled={isConfirmationActive ? isDisabled : false}
            />
          </>
        )}
      </CheckRequiredFields>

      <Dialog
        visible={visible}
        header={header}
        style={{ width: '60rem' }}
        breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {isConfirmationActive && (
          <div className="formgrid grid">
            {/* confirmationId */}
            {confirmationIdWrapper}

            {/* confirmationDate */}
            {confirmationDateWrapper}
          </div>
        )}

        <Divisions
          deliveryForecastId={deliveryForecastId}
          isConfirmationActive={isConfirmationActive}
        />
      </Dialog>
    </>
  )
}
